import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { AssessmentQuestion } from '../Constants/DASS21_QUESTIONS';

interface Answer {
  questionId: string;
  score: number;
}

interface AssessmentShellProps {
  assessmentType: string;
  questions: AssessmentQuestion[];
  scaleLabels: string[];
  onSubmit: (answers: Answer[]) => Promise<void>;
  companyName?: string;
}

export const AssessmentShell: React.FC<AssessmentShellProps> = ({
  assessmentType,
  questions,
  scaleLabels,
  onSubmit,
  companyName,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const total = questions.length;
  const question = questions[currentIndex];
  const progress = Math.round(((currentIndex + (answers[question?.id] !== undefined ? 1 : 0)) / total) * 100);
  const currentAnswer = question ? answers[question.id] : undefined;

  const handleAnswer = (score: number) => {
    setAnswers((prev) => ({ ...prev, [question.id]: score }));
  };

  const handleNext = () => {
    if (currentIndex < total - 1) setCurrentIndex((i) => i + 1);
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload: Answer[] = questions.map((q) => ({
        questionId: q.id,
        score: answers[q.id] ?? 0,
      }));
      await onSubmit(payload);
      setSubmitted(true);
    } catch {
      // error handled in parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const allAnswered = questions.every((q) => answers[q.id] !== undefined);
  const isLast = currentIndex === total - 1;

  if (submitted) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(248, 250, 252, 0.8)', px: 2 }}>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
            p: { xs: 4, sm: 5 },
            borderRadius: '24px',
            bgcolor: '#ffffff',
            boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'inline-flex',
              height: 64,
              width: 64,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: 'rgba(9, 55, 37, 0.1)',
              color: '#093725',
            }}
          >
            <CheckCircle2 size={36} />
          </Box>
          <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
            Thank you!
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Your responses have been submitted successfully. You may close this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', bgcolor: 'rgba(248, 250, 252, 0.6)' }}>
      {/* Branded header */}
      <Paper
        component='header'
        elevation={0}
        square
        sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#ffffff', px: 3, py: 2 }}
      >
        <Box sx={{ mx: 'auto', display: 'flex', maxWidth: 720, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant='caption' sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'primary.main', display: 'block' }}>
              {companyName ?? 'Wellness Screening'}
            </Typography>
            <Typography variant='subtitle1' sx={{ fontWeight: 800, color: 'text.primary' }}>
              {assessmentType} Assessment
            </Typography>
          </Box>
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {currentIndex + 1} / {total}
          </Typography>
        </Box>
      </Paper>

      {/* Progress bar */}
      <LinearProgress
        variant='determinate'
        value={progress}
        sx={{
          height: 6,
          bgcolor: 'rgba(226, 232, 240, 0.6)',
          '& .MuiLinearProgress-bar': {
            bgcolor: 'primary.main',
            transition: 'transform 0.3s ease',
          },
        }}
      />

      {/* Question card */}
      <Box component='main' sx={{ mx: 'auto', mt: 4, width: '100%', maxWidth: 720, flex: 1, px: 2, pb: 6 }}>
        <Paper
          elevation={0}
          variant='outlined'
          sx={{ borderRadius: '24px', bgcolor: '#ffffff', p: { xs: 3, sm: 4 }, boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}
        >
          <Typography variant='caption' sx={{ mb: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled', display: 'block' }}>
            Question {currentIndex + 1} of {total}
          </Typography>
          <Typography variant='h6' sx={{ mb: 4, fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
            {question?.text}
          </Typography>

          {/* Response options — 0-3 scale */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {scaleLabels.map((label, score) => {
              const selected = currentAnswer === score;
              return (
                <Box
                  key={score}
                  component='button'
                  type='button'
                  onClick={() => handleAnswer(score)}
                  sx={{
                    display: 'flex',
                    minHeight: 52,
                    width: '100%',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: '16px',
                    border: '2px solid',
                    px: 2.5,
                    py: 1.5,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'rgba(9, 55, 37, 0.05)' : '#ffffff',
                    '&:hover': {
                      borderColor: selected ? 'primary.main' : 'text.disabled',
                      bgcolor: selected ? 'rgba(9, 55, 37, 0.08)' : 'rgba(248, 250, 252, 0.8)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      height: 28,
                      width: 28,
                      flexShrink: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '2px solid',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? 'primary.main' : '#ffffff',
                      color: selected ? '#ffffff' : 'text.secondary',
                    }}
                  >
                    {score}
                  </Box>
                  <Typography variant='body2' sx={{ fontWeight: 500, color: selected ? 'primary.main' : 'text.primary' }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* Navigation */}
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            type='button'
            onClick={handleBack}
            disabled={currentIndex === 0}
            variant='outlined'
            startIcon={<ChevronLeft size={18} />}
            sx={{
              borderRadius: '14px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'rgba(248, 250, 252, 0.8)',
              },
            }}
          >
            Back
          </Button>

          {isLast ? (
            <Button
              type='button'
              onClick={handleSubmit}
              disabled={!allAnswered || isSubmitting}
              variant='contained'
              sx={{
                borderRadius: '14px',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                  <span>Submitting…</span>
                </Box>
              ) : (
                'Submit'
              )}
            </Button>
          ) : (
            <Button
              type='button'
              onClick={handleNext}
              disabled={currentAnswer === undefined}
              variant='contained'
              endIcon={<ChevronRight size={18} />}
              sx={{
                borderRadius: '14px',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

